import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { LogIn, UserPlus, Check, AlertTriangle, ShieldCheck, Copyright, Lock } from 'lucide-react'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'


function SectionHeading({ children }: { children: React.ReactNode }) {
    return (
        <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="font-display mt-14 mb-5"
            style={{ fontSize: 'clamp(1.4rem, 2.4vw, 1.7rem)', color: 'var(--color-ink-700)', letterSpacing: '-0.015em' }}
        >
            {children}
        </motion.h2>
    )
}

function NoticeBox({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
    return (
        <div
            className="my-6 px-5 py-4 flex gap-3.5"
            style={{ background: 'var(--color-paper-100)', borderLeft: '3px solid var(--color-gold-500)', borderRadius: '2px', color: 'var(--color-ink-600)' }}
        >
            {icon && <span style={{ color: 'var(--color-gold-700)', flexShrink: 0, marginTop: 2 }}>{icon}</span>}
            <div className="text-[15px] leading-relaxed">{children}</div>
        </div>
    )
}

const AUTHOR_GUIDELINES: [string, string][] = [
    ['Manuscript Preparation', 'Follow ISR guidelines. Templates are available on the Author Guidelines page.'],
    ['Submission', 'Manuscripts of articles and book reviews must be submitted only through online mode.'],
    ['Authorship', 'All authors must have contributed significantly and approved the final version.'],
    ['Originality', 'Manuscripts must be original and not under consideration elsewhere.'],
    ['Plagiarism', 'A plagiarism report is mandatory. Submissions without it will be rejected.'],
    ['Length', 'Articles 5,000–6,000 words including references; book reviews 1,200–1,500 words.'],
    ['Abstract & Keywords', 'Abstract ≤ 250 words; up to 5 keywords.'],
    ['References', 'APA style; recommended 15 references per article.'],
    ['Figures & Tables', 'Properly numbered and placed within the text.'],
    ['Ethics', 'Research must comply with ethical publishing standards.']
]

const UGC_POLICY: [string, string][] = [
    ['Definition', 'Use of another’s work without proper acknowledgment.'],
    ['Similarity Index', 'Manuscripts must be checked using approved software.'],
    ['Acceptable Similarity', 'Up to 10% for research articles.'],
    ['Consequences', 'Plagiarised manuscripts will be rejected.'],
    ['Self-Plagiarism', 'Previously published work is not accepted.']
]

const CHECKLIST = [
    'Manuscript meets the Author Guidelines.',
    'Word count within 5,000–6,000 words.',
    'References checked and limited to 15–20.',
    'Tables and figures properly labelled.',
    'Permissions obtained for all materials.',
    'Plagiarism report attached.',
    'Authors agree to journal policies.'
]

const PRIVACY: { heading: string; paras: string[] }[] = [
    {
        heading: 'Information Collection',
        paras: [
            'When authors submit a manuscript or research paper, ISR collects information including author name, designation, institute/university, email address, contact number, and manuscript details such as title, abstract, index terms and content.',
            'CSPG also records server log information — including IP address, cookies, requested pages and browser data — to ensure security and improve services.'
        ]
    },
    {
        heading: 'Use of Information',
        paras: [
            'Collected data may be used to operate and improve the CSPG website, enhance user experience, deliver services, and notify users about updates, deadlines, conferences and relevant content.',
            'ISR may share authors’ published paper details with indexing partners without prior notice.'
        ]
    },
    {
        heading: 'Information Sharing and Disclosure',
        paras: [
            'CSPG does not rent or sell personal information. Data may be shared with trusted partners under confidentiality agreements or where required to fulfil legal obligations.',
            'During mergers or acquisitions, information may be transferred as part of business operations.'
        ]
    },
    {
        heading: 'Changes to this Privacy Policy',
        paras: [
            'CSPG may update this policy and notify users of significant changes via email or website notices.'
        ]
    }
]

function DefinitionList({ items }: { items: [string, string][] }) {
    return (
        <ul className="mt-1" style={{ borderTop: '1px solid var(--color-rule)' }}>
            {items.map(([term, text], i) => (
                <motion.li
                    key={term}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: (i % 6) * 0.03 }}
                    className="py-4"
                    style={{ borderBottom: '1px solid var(--color-rule)' }}
                >
                    <span className="font-display" style={{ fontSize: '1rem', color: 'var(--color-ink-700)' }}>
                        {term}.
                    </span>{' '}
                    <span className="text-[15px]" style={{ color: 'var(--color-ink-600)' }}>
                        {text}
                    </span>
                </motion.li>
            ))}
        </ul>
    )
}

export default function Submission() {
    return (
        <>
            <SEO
                title="Submission"
                description="How to make a submission to the Indian Studies Review (ISR): login/register, author guidelines, UGC plagiarism policy, preparation checklist, copyright, consent and privacy statement."
                path="/submission"
            />
            <PageHeader
                eyebrow="For Authors"
                title="Login or Register to Make a Submission"
                lede="Authors are invited to make a submission to this journal by registering on this website."
            />

            <section className="section">
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="grid lg:grid-cols-[1fr_auto] gap-8 items-center p-6 md:p-8"
                        style={{ border: '1px solid var(--color-rule-strong)', background: 'var(--color-paper-50)' }}
                    >
                        <div className="prose-body" style={{ maxWidth: '42rem' }}>
                            <p>
                                All submissions will be assessed by an editor to determine whether
                                they meet the aims and scope of this journal. Submissions considered
                                suitable will be sent for peer review.
                            </p>
                            <p style={{ marginBottom: 0 }}>
                                All incomplete submissions will be deleted automatically without
                                further notice.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 lg:min-w-[12rem]">
                            <Link to="/dashboard/login" className="btn btn-gold inline-flex items-center justify-center gap-2">
                                <LogIn size={16} /> Login
                            </Link>
                            <Link to="/dashboard/register" className="btn btn-ghost inline-flex items-center justify-center gap-2">
                                <UserPlus size={16} /> Register
                            </Link>
                        </div>
                    </motion.div>

                    <NoticeBox icon={<ShieldCheck size={18} />}>
                        Before submitting, authors must obtain permission to publish any material
                        included in the submission. All listed authors must consent to authorship.
                    </NoticeBox>

                    <p className="prose-body mt-6">
                        An editor may desk-reject a submission if it does not meet minimum quality
                        standards. Authors are advised to ensure that the title, abstract, study
                        design and arguments are clearly structured.
                    </p>

                    <SectionHeading>Author Guidelines</SectionHeading>
                    <DefinitionList items={AUTHOR_GUIDELINES} />
                    <Link
                        to="/guidelines"
                        className="inline-flex items-center gap-1.5 mt-4 text-[12px] uppercase font-semibold"
                        style={{ letterSpacing: '0.14em', color: 'var(--color-gold-700)' }}
                    >
                        Full guidelines &amp; templates →
                    </Link>

                    <SectionHeading>UGC Plagiarism Policy</SectionHeading>
                    <p className="prose-body">
                        ISR follows the UGC (Promotion of Academic Integrity and Prevention of
                        Plagiarism in Higher Educational Institutions) Regulations, 2018.
                    </p>
                    <DefinitionList items={UGC_POLICY} />

                    <SectionHeading>Submission Preparation Checklist</SectionHeading>
                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
                        {CHECKLIST.map((c, i) => (
                            <motion.div
                                key={c}
                                initial={{ opacity: 0, x: -8 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                                className="flex gap-3 items-start py-2.5"
                                style={{ borderBottom: '1px solid var(--color-rule)' }}
                            >
                                <span
                                    className="inline-flex items-center justify-center flex-shrink-0 mt-0.5"
                                    style={{ width: '1.3rem', height: '1.3rem', background: 'var(--color-gold-700)', color: 'white', borderRadius: '50%' }}
                                >
                                    <Check size={13} strokeWidth={3} />
                                </span>
                                <span className="text-[15px]" style={{ color: 'var(--color-ink-600)' }}>
                                    {c}
                                </span>
                            </motion.div>
                        ))}
                    </div>
                    <NoticeBox icon={<AlertTriangle size={18} />}>
                        Submission does not guarantee publication. The Editorial Board reserves the
                        right to accept or reject manuscripts.
                    </NoticeBox>

                    <SectionHeading>Copyright &amp; Licensing</SectionHeading>
                    <div className="flex gap-3.5 prose-body">
                        <Copyright size={20} style={{ color: 'var(--color-gold-700)', flexShrink: 0, marginTop: 4 }} />
                        <p style={{ marginTop: 0 }}>
                            Copyright for articles published in ISR is retained by the author(s),
                            with first-publication rights granted to ISR. Authors retain copyright
                            but grant ISR an exclusive licence to publish and distribute the
                            manuscript.
                        </p>
                    </div>

                    <SectionHeading>Consent for Publication</SectionHeading>
                    <p className="prose-body">
                        By submitting, authors grant ISR the right to publish the manuscript in any
                        format and agree to its distribution mechanism.
                    </p>

                    <div
                        className="mt-16 p-7 md:p-9"
                        style={{ background: 'var(--color-paper-100)', border: '1px solid var(--color-rule)', borderRadius: '2px' }}
                    >
                        <div className="flex items-center gap-3">
                            <span
                                className="inline-flex items-center justify-center font-display"
                                style={{ width: '2.6rem', height: '2.6rem', background: 'var(--color-gold-700)', color: 'var(--color-paper-50)', borderRadius: '50%', fontSize: '0.85rem', letterSpacing: '0.05em' }}
                            >
                                <Lock size={16} />
                            </span>
                            <div>
                                <div className="kicker" style={{ margin: 0 }}>Privacy Statement</div>
                                <div className="font-display" style={{ fontSize: '1.2rem', color: 'var(--color-ink-700)' }}>
                                    Indian Studies Review (ISR)
                                </div>
                            </div>
                        </div>

                        <p className="prose-body mt-5">
                            This privacy statement outlines how cspgindia.in collects and utilises
                            personal information provided on our website,{' '}
                            <strong>www.cspgindia.in</strong>. It encompasses the options available
                            to you regarding the use of your personal data, access and updates.
                            Committed to safeguarding user privacy, ISR has adopted the following
                            terms to enhance your understanding of our privacy policy.
                        </p>

                        {PRIVACY.map((p) => (
                            <div key={p.heading} className="mt-6">
                                <h4 className="font-display" style={{ fontSize: '1.05rem', color: 'var(--color-ink-700)' }}>
                                    {p.heading}
                                </h4>
                                {p.paras.map((para, i) => (
                                    <p key={i} className="prose-body mt-2" style={{ fontSize: '14.5px' }}>
                                        {para}
                                    </p>
                                ))}
                            </div>
                        ))}

                        <p className="mt-6 text-[14px]" style={{ color: 'var(--color-ink-500)' }}>
                            For enquiries or suggestions regarding this policy, contact{' '}
                            <a href="mailto:admin@cspgindia.in" style={{ color: 'var(--color-gold-700)' }}>
                                admin@cspgindia.in
                            </a>
                            .
                        </p>
                        <div
                            className="mt-5 pt-4 text-[11px] uppercase font-semibold flex flex-wrap justify-between gap-2"
                            style={{ borderTop: '1px solid var(--color-rule)', letterSpacing: '0.14em', color: 'var(--color-ink-400)' }}
                        >
                            <span>Effective from 15 August 2023</span>
                            <span>© CSPG 2025</span>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
